import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";
import tenantApis from "src/api/tenant";
import { Tenant } from "src/types/tenant.types";
import { toast } from "react-toastify";
import { transactionStatuses, transactionTypes } from "src/constants/common";

// Transaction creation now happens in a modal from the list page (see
// TransactionCreateModal.tsx) — this route/component only ever handles
// edit mode.
const TransactionForm: React.FC = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { editTransaction, transactionDetails } = useAppSelector(
    (state) => state.transaction
  );
  const [paymentMethod, setPaymentMethod] = useState<string>(
    transactionDetails.data?.paymentMethod || "cash"
  );
  const [tenantId, setTenantId] = useState("");
  const [tenantLabel, setTenantLabel] = useState("");

  useEffect(() => {
    setPaymentMethod(transactionDetails.data?.paymentMethod || "cash");
  }, [transactionDetails.data?.paymentMethod]);

  // Seed the tenant picker once the transaction's own details load — the
  // list endpoint populates tenantId with the tenant doc, so its name is
  // already available without a separate lookup.
  useEffect(() => {
    const tenant = (transactionDetails.data as any)?.tenantId;
    if (tenant && typeof tenant === "object") {
      setTenantId(tenant._id || "");
      setTenantLabel(tenant.tenantName || "");
    }
  }, [transactionDetails.data]);

  useEffect(() => {
    if (transactionId) {
      dispatch(transactionActions.getTransactionDetailsRequest(transactionId));
    }
  }, [dispatch, transactionId]);

  useEffect(() => {
    if (transactionDetails.error) {
      toast.error("Failed to fetch transaction details");
    }
  }, [transactionDetails.error]);

  const fields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        label: "Transaction Type",
        name: "transactionType",
        type: "dropdown",
        options: transactionTypes.map((type) => ({
          label: type,
          value: type,
        })),
        defaultValue: "payment",
        validations: { required: true },
      },
      {
        label: "Payment Method",
        name: "paymentMethod",
        type: "dropdown",
        options: [
          { label: "Cash", value: "cash" },
          { label: "Online", value: "online" },
        ],
        defaultValue: "cash",
        validations: { required: true },
        onChange: (value) => setPaymentMethod(value),
      },
      {
        label: "Amount",
        name: "amount",
        type: "number",
        placeholder: "Enter Amount",
        validations: { required: true },
      },
      {
        label: "Status",
        name: "status",
        type: "dropdown",
        options: transactionStatuses.map((status) => ({
          label: status,
          value: status,
        })),
        defaultValue: "created",
        validations: { required: true },
      },
      {
        label: "Remarks",
        name: "remarks",
        type: "textarea",
        placeholder: "Add remarks (optional)",
      },
      {
        label: "Data (optional)",
        name: "data",
        type: "textarea",
        placeholder: "Add JSON string (optional)",
      },
    ];

    if (paymentMethod === "online") {
      baseFields.splice(1, 0, {
        label: "Payment Mode",
        name: "paymentMode",
        type: "dropdown",
        options: [
          { label: "PhonePe", value: "phonepay" },
          { label: "GPay", value: "gpay" },
          { label: "Paytm", value: "paytm" },
          { label: "Other", value: "other" },
        ],
        validations: { required: true },
      });
      baseFields.splice(2, 0, {
        label: "Reference Id",
        name: "referenceId",
        type: "text",
        placeholder: "Enter Reference Id",
        validations: { required: true },
      });
    }

    return baseFields;
  }, [paymentMethod]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTransaction.data) {
      toast.success("Transaction updated successfully");
      handleClose();
    } else if (editTransaction.error) {
      toast.error(`Error: ${editTransaction.error}`);
    }
  }, [editTransaction]);

  const handleClose = () => {
    dispatch(transactionActions.resetEditTransaction());
    navigate("/admin/transaction/list");
  };

  const handleSubmit = (data: Record<string, any>) => {
    const payload = { ...data };
    if (payload.paymentMethod !== "online") {
      delete payload.paymentMode;
      delete payload.referenceId;
    }
    payload.tenantId = tenantId || "";

    dispatch(
      transactionActions.editTransactionRequest({
        id: transactionDetails.data?._id || (transactionId as string),
        data: payload,
      })
    );
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Edit Transaction | Invyntra",
        description: "Edit transaction details.",
        keywords: "transactions, details, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Edit Transaction"
          showBack
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Transactions", path: "/admin/transaction/list" },
            { label: "Edit Transaction", path: "" },
          ]}
        />
        <Card className="space-y-4">
          <div>
            <SearchInput<Tenant>
              label="Tenant (optional)"
              placeholder="Search tenants by name…"
              value={tenantLabel}
              onSearch={async (query) => {
                const res: any = await tenantApis.getTenantList({
                  page: 1,
                  limit: 10,
                  search: query,
                });
                const list: Tenant[] = res?.data?.data || res?.data || [];
                return list.map((t) => ({
                  value: t._id || "",
                  label: t.tenantName || "",
                  data: t,
                }));
              }}
              onSelect={(option: SearchInputOption<Tenant>) => {
                setTenantId(option.value);
                setTenantLabel(option.label);
              }}
              onClear={() => {
                setTenantId("");
                setTenantLabel("");
              }}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Who this transaction is for. Leave blank if it isn't tied to a
              specific tenant.
            </p>
          </div>

          <Form
            initialState={transactionDetails.data as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/admin/transaction/list")}
            loading={editTransaction.loading || transactionDetails.loading}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionForm;
